/**
 * Тонкая обёртка над Telegram WebApp.
 *
 * Mini App должен работать и в обычном браузере: без этого локальная отладка
 * интерфейса требовала бы туннеля и публикации на каждое изменение вёрстки.
 * Поэтому все обращения к Telegram проходят через необязательный объект.
 */

interface TelegramWebAppUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
}

interface TelegramWebApp {
  initData: string;
  initDataUnsafe: { user?: TelegramWebAppUser; start_param?: string };
  colorScheme: 'light' | 'dark';
  ready: () => void;
  expand: () => void;
  close: () => void;
  BackButton: {
    show: () => void;
    hide: () => void;
    onClick: (handler: () => void) => void;
    offClick: (handler: () => void) => void;
  };
  HapticFeedback?: {
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
    impactOccurred: (style: 'light' | 'medium' | 'heavy') => void;
  };
  showConfirm?: (message: string, callback: (confirmed: boolean) => void) => void;
  requestContact?: (callback: (shared: boolean) => void) => void;
  openLink?: (url: string, options?: { try_instant_view?: boolean }) => void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export const webApp = window.Telegram?.WebApp;

/**
 * Скрипт Telegram подключается на любой странице, но вне мессенджера его
 * диалоги и кнопки только отправляют сообщения несуществующему окну: ответ
 * не приходит никогда. Признак настоящего запуска — подписанная initData.
 */
export const isInsideTelegram = Boolean(webApp?.initData);

/** Интерфейсные возможности Telegram — только когда они действительно работают. */
export const tg = isInsideTelegram ? webApp : undefined;

export function initTelegram(): void {
  webApp?.ready();
  webApp?.expand();
}

/**
 * Какому боту принадлежит это Mini App.
 *
 * Telegram передаёт идентификатор в start_param (ссылка вида
 * t.me/bot/app?startapp=<id>), но при открытии в браузере его нет —
 * тогда работает параметр адреса или значение из сборки.
 */
export function resolveClientBotId(): string | null {
  const fromUrl = new URLSearchParams(window.location.search).get('bot');
  const fromTelegram = webApp?.initDataUnsafe?.start_param;

  return fromUrl || fromTelegram || import.meta.env.VITE_CLIENT_BOT_ID || null;
}

export function hapticSuccess(): void {
  tg?.HapticFeedback?.notificationOccurred('success');
}

export function hapticError(): void {
  tg?.HapticFeedback?.notificationOccurred('error');
}

/**
 * Внешняя ссылка: в Telegram — системный браузер, иначе новая вкладка.
 *
 * Политика и оферта не должны подменять экран записи: иначе клиент потеряет
 * выбранное время, пока читает документ.
 */
export function openExternal(url: string): void {
  const absolute = url.startsWith('http') ? url : `${window.location.origin}${url}`;

  if (tg?.openLink) {
    tg.openLink(absolute);
    return;
  }

  window.open(absolute, '_blank', 'noopener');
}

/** Подтверждение: в Telegram — родное окно, в браузере — обычный confirm. */
export function confirmAction(message: string): Promise<boolean> {
  if (tg?.showConfirm) {
    return new Promise((resolve) => tg.showConfirm!(message, resolve));
  }

  return Promise.resolve(window.confirm(message));
}
